import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { useRouter } from '../RouterContext';

const PrivacyPage: React.FC = () => {
  const { navigate } = useRouter();

  return (
    <div style={s.page}>
      <div style={s.topbar}>
        <div style={s.topbarInner}>
          <button onClick={() => navigate('home')} style={s.back}>
            <ArrowLeft size={16} />
            Voltar
          </button>
          <span style={s.logoMark}>2GF</span>
        </div>
      </div>

      <div style={s.container}>
        <div style={s.header}>
          <span style={s.label}>Proteção de Dados</span>
          <h1 style={s.title}>Política de Privacidade</h1>
          <p style={s.lastUpdate}>Última atualização: Janeiro de 2026</p>
        </div>

        <div style={s.content}>
          <p style={s.intro}>
            A 2GF Innovation Systems respeita a sua privacidade e está empenhada em proteger os seus dados pessoais.
            Esta política informa-o sobre como tratamos os seus dados pessoais quando visita o nosso website e sobre
            os seus direitos de privacidade.
          </p>

          <Section title="1. Responsável pelo Tratamento">
            <p style={s.p}>
              <strong>2GF Innovation Systems</strong><br />
              NIF: 516285858<br />
              Morada: Centro de Inovação Empresarial da Covilhã, R. António Augusto de Aguiar, 6200-053 Covilhã, Portugal<br />
              Email: digital@2gfinnovationsystems.pt<br />
              Telefone: +351 969 059 898
            </p>
          </Section>

          <Section title="2. Dados Pessoais que Recolhemos">
            <p style={s.p}>Podemos recolher, utilizar, armazenar e transferir diferentes tipos de dados pessoais:</p>
            <SubSection title="2.1 Dados de Identificação">
              <ul style={s.ul}>
                <li>Nome completo</li>
                <li>Endereço de email</li>
                <li>Número de telefone</li>
                <li>Nome da empresa e função/cargo</li>
              </ul>
            </SubSection>
            <SubSection title="2.2 Dados Técnicos">
              <ul style={s.ul}>
                <li>Endereço IP</li>
                <li>Tipo e versão do navegador</li>
                <li>Sistema operativo e plataforma</li>
                <li>Fuso horário e localização</li>
              </ul>
            </SubSection>
            <SubSection title="2.3 Dados de Utilização">
              <ul style={s.ul}>
                <li>Informações sobre como utiliza o nosso website</li>
                <li>Produtos e serviços visualizados</li>
                <li>Páginas visitadas</li>
              </ul>
            </SubSection>
          </Section>

          <Section title="3. Como Utilizamos os Seus Dados">
            <ul style={s.ul}>
              <li>Responder às suas solicitações através do formulário de contacto ou por email</li>
              <li>Prestar os serviços que solicitou</li>
              <li>Comunicar informações sobre os nossos produtos e serviços, quando consentido</li>
              <li>Analisar e melhorar a experiência do website</li>
              <li>Cumprir obrigações legais ou regulamentares</li>
            </ul>
          </Section>

          <Section title="4. Fundamentos Legais para o Tratamento">
            <ul style={s.ul}>
              <li><strong>Consentimento</strong> — quando nos deu consentimento específico</li>
              <li><strong>Execução de contrato</strong> — quando necessário para a execução de um contrato</li>
              <li><strong>Interesses legítimos</strong> — quando temos um interesse legítimo no tratamento</li>
              <li><strong>Obrigações legais</strong> — quando exigido por lei</li>
            </ul>
          </Section>

          <Section title="5. Divulgação dos Seus Dados">
            <p style={s.p}>Podemos partilhar os seus dados com:</p>
            <ul style={s.ul}>
              <li>Prestadores de serviços de TI e administração de sistemas</li>
              <li>Prestadores de serviços de análise de websites</li>
              <li>Autoridades reguladoras e outras autoridades competentes</li>
              <li>Consultores profissionais (advogados, auditores, seguradoras)</li>
            </ul>
            <p style={s.p}>
              Exigimos que todas as terceiras partes respeitem a segurança dos seus dados e os tratem de acordo com a lei.
            </p>
          </Section>

          <Section title="6. Segurança dos Dados">
            <p style={s.p}>
              Implementámos medidas de segurança adequadas para evitar que os seus dados pessoais sejam acidentalmente
              perdidos, utilizados ou acedidos de forma não autorizada, alterados ou divulgados. O acesso aos dados
              é limitado a colaboradores e terceiros com necessidade de os conhecer.
            </p>
          </Section>

          <Section title="7. Período de Conservação">
            <p style={s.p}>
              Conservamos os seus dados pessoais apenas durante o tempo necessário para cumprir os fins para os quais
              foram recolhidos, incluindo requisitos legais, contabilísticos ou de comunicação.
            </p>
          </Section>

          <Section title="8. Os Seus Direitos">
            <ul style={s.ul}>
              <li><strong>Direito de acesso</strong> — pode solicitar acesso aos seus dados pessoais</li>
              <li><strong>Direito de retificação</strong> — pode solicitar a correção dos seus dados</li>
              <li><strong>Direito ao apagamento</strong> — pode solicitar que apaguemos os seus dados</li>
              <li><strong>Direito de oposição</strong> — pode opor-se ao tratamento dos seus dados</li>
              <li><strong>Direito de limitação</strong> — pode solicitar a restrição do tratamento</li>
              <li><strong>Direito à portabilidade</strong> — pode solicitar a transferência dos seus dados</li>
              <li><strong>Direito de retirar consentimento</strong> — pode retirar o consentimento a qualquer momento</li>
            </ul>
            <p style={s.p}>
              Para exercer os seus direitos, contacte-nos em <a href="mailto:digital@2gfinnovationsystems.pt" style={s.a}>digital@2gfinnovationsystems.pt</a> ou por telefone: +351 969 059 898.
            </p>
          </Section>

          <Section title="9. Direito de Reclamação">
            <p style={s.p}>
              Tem o direito de apresentar uma reclamação à Comissão Nacional de Proteção de Dados (CNPD) se considerar
              que o tratamento dos seus dados viola a legislação aplicável.
            </p>
            <p style={s.p}>
              <strong>CNPD</strong><br />
              Av. D. Carlos I, 134 — 1.º, 1200-651 Lisboa<br />
              Telefone: +351 213 928 400<br />
              Email: <a href="mailto:geral@cnpd.pt" style={s.a}>geral@cnpd.pt</a>
            </p>
          </Section>

          <Section title="10. Alterações a Esta Política">
            <p style={s.p}>
              Reservamo-nos o direito de atualizar esta política a qualquer momento. Quaisquer alterações serão
              publicadas nesta página com a data de atualização.
            </p>
          </Section>

          <Section title="11. Contacte-nos">
            <p style={s.p}>
              2GF Innovation Systems<br />
              Email: <a href="mailto:digital@2gfinnovationsystems.pt" style={s.a}>digital@2gfinnovationsystems.pt</a><br />
              Telefone: +351 969 059 898<br />
              Morada: Centro de Inovação Empresarial da Covilhã, R. António Augusto de Aguiar, 6200-053 Covilhã, Portugal
            </p>
          </Section>
        </div>
      </div>

      <div style={s.footer}>
        <span>© {new Date().getFullYear()} 2GF Innovation Systems · NIF 516285858</span>
      </div>
    </div>
  );
};

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div style={s.section}>
    <h2 style={s.sectionTitle}>{title}</h2>
    {children}
  </div>
);

const SubSection: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div style={s.subsection}>
    <h3 style={s.subsectionTitle}>{title}</h3>
    {children}
  </div>
);

const s: { [key: string]: React.CSSProperties } = {
  page: {
    background: '#ffffff',
    minHeight: '100vh',
  },
  topbar: {
    background: '#020F1E',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
    padding: '1rem 0',
  },
  topbarInner: {
    maxWidth: '800px',
    margin: '0 auto',
    padding: '0 2rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  back: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    fontFamily: "'IBM Plex Sans', sans-serif",
    fontSize: '0.8rem',
    fontWeight: 600,
    color: 'rgba(255,255,255,0.6)',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    letterSpacing: '0.05em',
    padding: 0,
  },
  logoMark: {
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    fontSize: '1.25rem',
    fontWeight: 900,
    color: '#0057FF',
    letterSpacing: '-0.02em',
  },
  container: {
    maxWidth: '800px',
    margin: '0 auto',
    padding: '4rem 2rem 6rem',
  },
  header: {
    marginBottom: '3rem',
    paddingBottom: '3rem',
    borderBottom: '1px solid #E8EDF3',
  },
  label: {
    fontFamily: "'IBM Plex Sans', sans-serif",
    fontSize: '0.7rem',
    fontWeight: 600,
    letterSpacing: '0.2em',
    textTransform: 'uppercase' as const,
    color: '#0057FF',
    display: 'block',
    marginBottom: '0.75rem',
  },
  title: {
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    fontSize: 'clamp(2rem, 4vw, 2.75rem)',
    fontWeight: 800,
    color: '#0D1B2A',
    letterSpacing: '-0.03em',
    lineHeight: 1.1,
    marginBottom: '0.75rem',
  },
  lastUpdate: {
    fontSize: '0.875rem',
    color: '#94A3B8',
    margin: 0,
  },
  intro: {
    fontSize: '1rem',
    color: '#64748B',
    lineHeight: 1.85,
    marginBottom: '3rem',
    paddingBottom: '3rem',
    borderBottom: '1px solid #E8EDF3',
  },
  content: {},
  section: {
    marginBottom: '2.5rem',
    paddingBottom: '2.5rem',
    borderBottom: '1px solid #F1F5F9',
  },
  sectionTitle: {
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    fontSize: '1.1rem',
    fontWeight: 700,
    color: '#0D1B2A',
    letterSpacing: '-0.01em',
    marginBottom: '1rem',
  },
  subsection: {
    marginTop: '1.25rem',
  },
  subsectionTitle: {
    fontFamily: "'IBM Plex Sans', sans-serif",
    fontSize: '0.85rem',
    fontWeight: 600,
    color: '#334155',
    marginBottom: '0.5rem',
  },
  p: {
    fontSize: '0.9rem',
    color: '#64748B',
    lineHeight: 1.85,
    marginBottom: '0.75rem',
  },
  ul: {
    fontSize: '0.9rem',
    color: '#64748B',
    lineHeight: 1.85,
    paddingLeft: '1.25rem',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.35rem',
  },
  a: {
    color: '#0057FF',
    textDecoration: 'none',
  },
  footer: {
    background: '#020F1E',
    padding: '1.5rem 2rem',
    textAlign: 'center' as const,
    fontSize: '0.8rem',
    color: 'rgba(255,255,255,0.25)',
  },
};

export default PrivacyPage;
